import React from "react";
import PlaceholderImage from '../PlaceholderImage';

const WorkoutStep = ({
  exercise,
  handleNext,
  handlePrevious,
  currentStep,
  totalSteps,
  selectedMuscles = [],
  workoutName,
}) => {
  // Safety check first - before any console.log statements
  if (!exercise) {
    console.log('No exercise provided to WorkoutStep');
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">Loading workout...</h2>
          <p className="text-gray-500">Please wait while we load your workout.</p>
        </div>
      </div>
    );
  }
  
  console.log('WorkoutStep received exercise:', exercise);
  console.log('WorkoutStep exercise.images:', exercise.images);
  console.log('WorkoutStep exercise.repRange:', exercise.repRange);
  console.log('WorkoutStep currentStep:', currentStep);
  console.log('WorkoutStep totalSteps:', totalSteps);

  const today = new Date();
  const month = today.toLocaleString("default", { month: "long" });
  const day = today.getDate();
  const displayWorkoutName = workoutName || `${selectedMuscles.map(group => group.charAt(0).toUpperCase() + group.slice(1)).join(", ")} Workout on ${month} ${day}`;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Workout Name */}
      <div className="text-center mb-4">
        <h2 className="text-xl font-semibold text-gray-700">{displayWorkoutName}</h2>
      </div>

      {/* Visual Group */}
      <div className="bg-gray-50 border border-gray-300 p-6 rounded-2xl shadow-lg space-y-4 max-w-3xl mx-auto">
        {/* Exercise Title & Step Count */}
        <div className="text-center">
          <h3 className="text-2xl font-bold">{exercise.name}</h3>
          <p className="text-sm text-gray-500">
            Exercise {currentStep + 1} of {totalSteps}
          </p>
        </div>

        {/* Images */}
        <div className="flex justify-center gap-4 flex-wrap">
          {exercise.images && exercise.images.length > 0 ? (
            exercise.images.map((img, index) => (
              img === "placeholder.jpg" ? (
                <PlaceholderImage 
                  key={index}
                  className="w-48 h-48 rounded shadow"
                />
              ) : (
                <img
                  key={index}
                  src={`/exercise-images/${img}`}
                  alt={`${exercise.name} ${index + 1}`}
                  className="w-48 h-48 object-cover rounded shadow"
                />
              )
            ))
          ) : (
            <div className="w-48 h-48 bg-gray-200 rounded shadow flex items-center justify-center">
              <span className="text-gray-400 text-lg">📷</span>
            </div>
          )}
        </div>

        {/* Training Guidance */}
        {exercise.repRange && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-center text-gray-700">Training Guidance</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(exercise.repRange).map(([goal, range]) => {
                const goalIcons = {
                  'strength': '💪',
                  'muscle_building': '🏋️',
                  'endurance': '🏃',
                  'power': '⚡',
                  'flexibility': '🧘',
                  'mobility': '🔄',
                  'recovery': '😌',
                  'cardio': '❤️',
                  'hiit': '🔥',
                  'conditioning': '⚡',
                  'explosive': '💥',
                  'technique': '🎯',
                  'stability': '⚖️',
                  'activation': '🔋'
                };
                
                const goalLabels = {
                  'strength': 'Strength',
                  'muscle_building': 'Muscle Building',
                  'endurance': 'Endurance',
                  'power': 'Power',
                  'flexibility': 'Flexibility',
                  'mobility': 'Mobility',
                  'recovery': 'Recovery',
                  'cardio': 'Cardio',
                  'hiit': 'HIIT',
                  'conditioning': 'Conditioning',
                  'explosive': 'Explosive',
                  'technique': 'Technique',
                  'stability': 'Stability',
                  'activation': 'Activation'
                };

                return (
                  <div key={goal} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{goalIcons[goal] || '💪'}</span>
                      <h5 className="font-semibold text-gray-800">{goalLabels[goal] || goal}</h5>
                    </div>
                    <p className="text-sm text-gray-600">{range}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation Controls */}
        <div className="flex justify-between mt-4">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded shadow disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700"
          >
            {currentStep + 1 < totalSteps ? "Next Exercise" : "Finish Workout"}
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-gray-50 p-4 rounded space-y-2">
        <h4 className="font-semibold text-lg">Instructions</h4>
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
          {exercise.instructions.map((step, idx) => (
            <li key={idx}>{step}</li>
          ))}
        </ol>
      </div>

      {/* Exercise Metadata */}
      <div className="bg-gray-50 p-4 rounded space-y-1 text-sm text-gray-700">
        <h4 className="font-semibold text-lg mb-1">Exercise Details</h4>
        <p><strong>Primary Muscle:</strong> {exercise.primaryMuscles?.join(", ") || "N/A"}</p>
        <p><strong>Secondary Muscle:</strong> {exercise.secondaryMuscles?.join(", ") || "None"}</p>
        <p><strong>Equipment:</strong> {exercise.equipment || "Bodyweight"}</p>
        <p><strong>Level:</strong> {exercise.level || "All Levels"}</p>
      </div>
    </div>
  );
};

export default WorkoutStep;
